import { resolveActiveAuditOverrides } from './truthAttestations';
import { classifyDistanceMention } from './distanceRoles';
/**
 * R-79 — Cross-report canonical fact reconciler.
 * Read-only: extracts facts from reports, compares against SED + intent + provider truth.
 */
import type { SiteAuditOverride, SiteEntity } from '../../types/site';
import type { SiteRagReport } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type {
  CanonicalFactKey,
  CanonicalFactSet,
  CanonicalFactValue,
  CitationRange,
  ReconciliationFinding,
  ReconciliationReport,
} from '../../types/auditCanonicalFacts';
import { capacityAnchors, capacitySsotFromPhases } from './siteReportAuditor';
import { LINE_LENGTH_DISTANCE_CONTEXT_RE, NON_FACILITY_MW_CONTEXT_RE, isComparisonBoundPrefix, isNonFacilityCapacityMention, capacityQuantityFieldLabel } from './capacityPolysemy';
import { effectivePhases } from '../projectIntent';
import { waterSourceKinds } from '../intent/waterSource';
import { isUnavailableFloodZone } from './floodZoneAvailability';

/** Prose spellings the waterSourceType extractor emits, per intent kind —
 *  keeps the multi-select acceptance in lockstep with the pattern table. */
const WATER_KIND_PROSE_VALUES: Record<string, string[]> = {
  municipal: ['municipal', 'potable'],
  groundwater: ['groundwater', 'well water'],
  surface: ['surface water'],
  reuse: ['reuse', 'recycled'],
};

const MI_TO_KM = 1.60934;
const GW_TO_MW = 1000;
const HA_TO_ACRES = 2.47105;

const FACT_LABELS: Record<CanonicalFactKey, string> = {
  capacityMW: 'Capacity (MW)',
  interconnectionDistanceKm: 'Interconnection / substation distance (km)',
  floodZone: 'FEMA flood zone',
  acreage: 'Site acreage',
  wetlandPercent: 'Wetland coverage (%)',
  nearestSubstation: 'Nearest substation',
  voltageKV: 'Substation voltage (kV)',
  waterSourceType: 'Water source type',
  nuclearProximityKm: 'Nearest nuclear reactor distance (km)',
  gridOperator: 'Grid operator / ISO',
  censusTract: 'Census tract',
};

interface PatternDef {
  re: RegExp;
  contextRe?: RegExp;
  /**
   * K/L — polysemy guard: a line matching this pattern is about a DIFFERENT
   * quantity that shares the fact's unit (grid headroom in MW, a fiber
   * lateral in km) and is skipped entirely — it is neither a claim nor
   * corroboration. Shared failure mode with siteReportAuditor's fact table.
   */
  negativeContextRe?: RegExp;
  parse: (m: RegExpMatchArray, line: string) => { value: string | number; units?: string } | null;
}

/**
 * K — "200–500 MW" endpoints are a BAND, not value claims. Matched against
 * the text immediately before the captured number.
 */
const RANGE_ENDPOINT_RE = /[\d,]+(?:\.\d+)?\s*[-–—]\s*$/;

function isRangeEndpoint(line: string, matchIndex: number | undefined): boolean {
  return typeof matchIndex === 'number' && RANGE_ENDPOINT_RE.test(line.slice(0, matchIndex));
}

function lineRange(text: string, lineIndex: number): CitationRange {
  return { startLine: lineIndex + 1, endLine: lineIndex + 1 };
}

function pushFact(
  set: CanonicalFactSet,
  key: CanonicalFactKey,
  occurrence: CanonicalFactValue,
): void {
  const list = set[key] ?? [];
  const dup = list.some(
    (f) => f.value === occurrence.value && f.units === occurrence.units,
  );
  if (!dup) list.push(occurrence);
  set[key] = list;
}

/**
 * F — substation-name sanitizer. Rejects captures that are sentence
 * fragments, numbers/distances, or grid-project descriptors rather than
 * names; trims a trailing voltage qualifier ("Moss Bluff 230 kV" → keeps the
 * name, the voltage is `voltageKV`'s fact). Returns null to drop the mention.
 */
const SUBSTATION_REJECT_RE = /^(is|was|are|were|located|situated|approximately|about|roughly|the|a|an|planned|proposed|future|new|existing|nearby|adjacent)\b|^\d|\b(km|mi|miles?)\b|\bexpansion\b|\bupgrade\b/i;

/**
 * DESCRIPTOR-ONLY rejection for STRUCTURED name fields (2026-08-14).
 *
 * The full reject list above mixes two jobs: dropping grid-project
 * DESCRIPTORS ("Planned 230kV Expansion") and dropping PROSE-CAPTURE
 * artifacts (leading verbs/articles/distances — "is 2", "the", "located").
 * The prose heuristics are meaningless for a structured `nearestSubstationName`
 * field and actively harmful there: real substations are named "New Madrid",
 * "The Junction", "Adjacent Tap 4". Rejecting those would DELETE the fact,
 * and a deleted fact cannot conflict — so a hallucinated name elsewhere would
 * stop producing any cross-report finding at all (gate weakening).
 * The structured path therefore applies ONLY descriptor rejection.
 */
const SUBSTATION_DESCRIPTOR_RE = /\b(?:expansion|upgrade|rebuild|reconductoring)\b|^(?:planned|proposed|future|potential)\b/i;

/** Sanitize a name from a STRUCTURED field (not a prose capture). */
function sanitizeStructuredSubstationName(raw: string): { value: string } | null {
  const name = raw.trim().replace(/\s+/g, ' ').replace(/\s+[\d,.]+\s*kV$/i, '').trim();
  if (name.length < 2) return null;
  if (!/[A-Za-z]/.test(name)) return null;
  if (SUBSTATION_DESCRIPTOR_RE.test(name)) return null;
  return { value: name };
}

function sanitizeSubstationName(raw: string): { value: string } | null {
  let name = raw.trim().replace(/\s+/g, ' ');
  // Drop a trailing voltage qualifier: "TAP143264 230 kV" → "TAP143264".
  name = name.replace(/\s+[\d,.]+\s*kV$/i, '').trim();
  if (name.length < 2) return null;
  if (SUBSTATION_REJECT_RE.test(name)) return null;
  // A "name" with no letters is a stray number.
  if (!/[A-Za-z]/.test(name)) return null;
  return { value: name };
}

function parseDistanceKm(raw: string, unit?: string): number | null {
  const n = parseFloat(raw.replace(/,/g, ''));
  if (!Number.isFinite(n)) return null;
  const u = (unit ?? 'km').toLowerCase();
  if (/^mi|mile/.test(u)) return n * MI_TO_KM;
  return n;
}

function parseCapacityMw(raw: string, unit?: string): number | null {
  const n = parseFloat(raw.replace(/,/g, ''));
  if (!Number.isFinite(n)) return null;
  const u = (unit ?? 'mw').toLowerCase();
  if (u === 'gw') return n * GW_TO_MW;
  return n;
}

function parseAreaAcres(raw: string, unit?: string): number | null {
  const n = parseFloat(raw.replace(/,/g, ''));
  if (!Number.isFinite(n)) return null;
  const u = (unit ?? 'ac').toLowerCase();
  if (/^ha|hectare/.test(u)) return n * HA_TO_ACRES;
  return n;
}

const PROSE_PATTERNS: Record<CanonicalFactKey, PatternDef[]> = {
  capacityMW: [
    {
      re: /\b([\d,]+(?:\.\d+)?)\s*(MW|GW)\b/i,
      contextRe: /(\bMW\b|capacity|power requirement|total power|campus|facility)/i,
      // K — FACILITY capacity only. SHARED with tier-2e's
      // `internalNumberReconciler` since 2026-08-14: this guard lived here
      // alone, so the within-report engine still bucketed grid headroom as
      // facility capacity and the two asserted opposite truths about one
      // figure. Both now import the single definition.
      negativeContextRe: NON_FACILITY_MW_CONTEXT_RE,
      parse: (m) => {
        const v = parseCapacityMw(m[1], m[2]);
        return v != null ? { value: v, units: 'MW' } : null;
      },
    },
  ],
  interconnectionDistanceKm: [
    {
      re: /\b([\d,]+(?:\.\d+)?)\s*(km|mi|miles?)\b/i,
      contextRe: /(interconnect|substation|transmission|grid access|nearest sub)/i,
      // L — a fiber-lateral length or IX distance on a grid-flavoured line is
      // a DIFFERENT distance. Redhead: an 8.05 km fiber corridor was flagged
      // against the 2.9 km substation tier-1 value, and "Regenerate with
      // verified value" would have rewritten a CORRECT fiber claim.
      // + line-length guard (2026-08-18, Redhead `stne8w`) — SHARED with
      // tier-2e's internalNumberReconciler: a "3.4 km transmission line to a
      // 230 kV corridor" is a build quantity, not the substation distance.
      negativeContextRe: LINE_LENGTH_DISTANCE_CONTEXT_RE,
      parse: (m) => {
        const v = parseDistanceKm(m[1], m[2]);
        return v != null ? { value: v, units: 'km' } : null;
      },
    },
  ],
  floodZone: [
    {
      re: /\bzone\s+(A(?:E|H|O)?|AE|AO|AH|VE|V|X|D|B|C)\b/i,
      contextRe: /(flood|FEMA|SFHA)/i,
      parse: (m) => ({ value: m[1].toUpperCase() }),
    },
    {
      re: /\bFEMA\s+(?:100[- ]yr\s+)?(?:flood\s+)?zone\s+(A(?:E|H|O)?|AE|AO|AH|VE|V|X|D|B|C)\b/i,
      parse: (m) => ({ value: m[1].toUpperCase() }),
    },
  ],
  acreage: [
    {
      re: /\b([\d,]+(?:\.\d+)?)\s*(acres?|ac\.?|ha|hectares?)\b/i,
      // The bare `acres` context made EVERY acres line a site-acreage claim,
      // so "65.7 acres of jurisdictional wetlands" was extracted as the SITE
      // acreage and conflicted with the real 1,170-acre parcel (Redhead,
      // 2026-08-12). Site acreage needs site/parcel language on the line —
      // and a wetland/floodplain line is talking about a sub-area.
      // (Buildable-footprint mentions stay IN: their drift vs the parcel
      // geometry is a pinned warn-level review signal — R-78 WARN_ONLY.)
      contextRe: /(site (area|acreage)|parcel|footprint|total (site|acreage)|acre site)/i,
      negativeContextRe: /(wetland|floodplain|flood\s*zone)/i,
      parse: (m) => {
        const v = parseAreaAcres(m[1], m[2]);
        return v != null ? { value: v, units: 'acres' } : null;
      },
    },
  ],
  wetlandPercent: [
    {
      re: /\b([\d,]+(?:\.\d+)?)\s*%\s*(?:of\s+(?:the\s+)?(?:site|parcel))?\s*wetland/i,
      parse: (m) => ({ value: parseFloat(m[1]), units: '%' }),
    },
    {
      re: /wetland[^.\n]{0,80}\b([\d,]+(?:\.\d+)?)\s*%/i,
      parse: (m) => ({ value: parseFloat(m[1]), units: '%' }),
    },
  ],
  nearestSubstation: [
    // F — hardened 2026-08-12. The old `[:\s]+` accepted a bare space, so
    // "the nearest substation is 2.9 km away" captured the NAME "is 2" (the
    // '.' in 2.9 terminated the capture) and fed it into the Fix-value picker
    // and R-78 precedence as a report-claimed substation name. A name is now
    // only captured after an explicit separator or naming verb, and the
    // capture itself must not be a verb fragment, a number, a distance, or a
    // grid-project descriptor ("Planned 230kV Expansion").
    {
      re: /(?:nearest|closest)\s+substation\s*(?:[:—–-]|\bis\s+(?:named|called)\s+|\bnamed\s+|\bcalled\s+)\s*([A-Za-z][A-Za-z0-9\s\-/.]{2,60}?)(?:\s*\(|\.|,|$)/i,
      parse: (m) => sanitizeSubstationName(m[1]),
    },
    // Name-before-noun prose: "the TAP143264 substation", "the Moss Bluff
    // 230 kV substation" (voltage tail dropped by the sanitizer).
    {
      re: /\bthe\s+([A-Z][A-Za-z0-9\-/.]*(?:\s+[A-Z0-9][A-Za-z0-9\-/.]*){0,4})\s+substation\b/,
      parse: (m) => sanitizeSubstationName(m[1]),
    },
  ],
  voltageKV: [
    // Slashed dual-voltage pair FIRST ("230/69 kV"): the generic pattern
    // below only sees the number touching "kV" (the LOW side), so the high
    // side of a pair was invisible and could never corroborate tier-1
    // (Redhead RCA 2026-08-13). This pattern surfaces the HIGH side; the
    // generic one still captures the low side on the same line.
    {
      re: /\b([\d,]+(?:\.\d+)?)\s*\/\s*[\d,]+(?:\.\d+)?\s*kV\b/i,
      contextRe: /(substation|transmission|voltage|grid)/i,
      parse: (m) => ({ value: parseFloat(m[1].replace(/,/g, '')), units: 'kV' }),
    },
    {
      re: /\b([\d,]+(?:\.\d+)?)\s*kV\b/i,
      contextRe: /(substation|transmission|voltage|grid)/i,
      parse: (m) => ({ value: parseFloat(m[1].replace(/,/g, '')), units: 'kV' }),
    },
  ],
  waterSourceType: [
    {
      re: /\b(municipal|groundwater|surface water|recycled|reuse|potable|well water)\b/i,
      contextRe: /(water (source|supply|rights)|cooling water|withdrawal)/i,
      // POLYSEMY GUARD (2026-08-14, Redhead): "groundwater" in a report is
      // usually NOT a statement of the project's supply. The live false
      // positive flagged a cross-report conflict from three contextual
      // senses, none of them a source claim:
      //   · RIGHTS-CATEGORY COMPOUND — "unpermitted surface/groundwater
      //     rights" (note "groundwater rights" trivially satisfies the
      //     contextRe's own 'water rights' alternative)
      //   · LEGAL DOCTRINE — "riparian doctrine for surface water and rule
      //     of capture for groundwater" (Louisiana water law)
      //   · GEOTECHNICAL — "subsidence risk, and groundwater conditions is
      //     critical for foundation design"
      // A genuine claim ("the plan assumes groundwater as the primary
      // cooling water supply") carries none of these markers and still
      // extracts — so an UNDECLARED source still conflicts.
      negativeContextRe: /(riparian|rule of capture|doctrine|water law|civil code|statut|jurisprudence|regulatory (?:oversight|framework)|foundation|geotechnical|subsidence|bearing capacity|borehole|dewatering|aquifer (?:conditions|characteriz)|\w\s*\/\s*(?:groundwater|surface water)|(?:groundwater|surface water)\s*\/\s*\w)/i,
      parse: (m) => ({ value: m[1].toLowerCase() }),
    },
  ],
  nuclearProximityKm: [
    {
      re: /\b([\d,]+(?:\.\d+)?)\s*(km|mi|miles?)\b/i,
      contextRe: /(nuclear|reactor|NRC)/i,
      parse: (m) => {
        const v = parseDistanceKm(m[1], m[2]);
        return v != null ? { value: v, units: 'km' } : null;
      },
    },
  ],
  gridOperator: [
    // H — ISO/RTO ONLY (2026-08-12). The old token list mixed utilities
    // (Entergy, AEP, Duke, Southern Co) with ISOs, so "service from Entergy"
    // (the load-serving utility INSIDE MISO) manufactured a cross-report
    // conflict against "located in MISO" — a category error, both true. The
    // fact's truth source is `sed.grid.data.isoRegion`, so only ISO/RTO
    // tokens belong here; utility names are a different fact the qualitative
    // reviewer already covers. ERCOT-vs-MISO drift still blocks.
    {
      re: /\b(MISO|PJM|ERCOT|SPP|CAISO|NYISO|ISO-NE|WECC)\b/i,
      contextRe: /(ISO|RTO|market|balancing authority|grid operator|transmission operator|interconnection region)/i,
      parse: (m) => ({ value: m[1] }),
    },
  ],
  censusTract: [
    {
      re: /\bcensus tract[:\s]+([\d.]+)\b/i,
      parse: (m) => ({ value: m[1] }),
    },
  ],
};

function flattenToText(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return v.map(flattenToText).join('\n');
  if (typeof v === 'object') return Object.entries(v as Record<string, unknown>).map(([key, value]) => {
    const label = capacityQuantityFieldLabel(key);
    const text = flattenToText(value);
    return label && (typeof value === 'string' || typeof value === 'number')
      ? `${label}: ${text}` : text;
  }).join('\n');
  return String(v);
}

function extractSections(report: SiteRagReport): Array<{ id: string; content: string }> {
  const out: Array<{ id: string; content: string }> = [];
  const r = report as unknown as Record<string, unknown>;
  const knownStringSections = [
    'executiveSummary', 'summary', 'sitingNotes', 'developmentPathway', 'projectIntentOverview',
    'executive', 'stakeholderMap', 'perBtmSentiment', 'themeDeepDives', 'trendAnalysis',
    'comparableSiteBenchmark', 'mitigationPlaybook', 'sentimentAppendix', 'appendix',
    'constructabilityAssessment', 'costRangeEstimate', 'maxCampusCapacity',
  ];
  const knownObjectSections = [
    'conclusion', 'gridStudy', 'transportStudy', 'connectivityStudy', 'climateSevereWeather',
    'infrastructureInterconnection', 'insights', 'projectContext', 'powerAnalysis', 'interconnectionAnalysis',
    // 2026-08-13 (Redhead water RCA) — lockstep with siteReportAuditor: the
    // water report's real narrative lives here, invisible to every text check.
    'waterAnalysis', 'editorialNarrative',
  ];
  for (const k of knownStringSections) {
    if (typeof r[k] === 'string') out.push({ id: k, content: r[k] as string });
  }
  for (const k of knownObjectSections) {
    if (r[k] && typeof r[k] === 'object') out.push({ id: k, content: flattenToText(r[k]) });
  }
  for (const ans of report.customBriefAnswers ?? []) {
    out.push({
      id: `customBriefQ_${ans.questionId}`,
      content: [ans.question, ans.findings, ans.evidence].filter(Boolean).join('\n'),
    });
  }
  return out;
}

function reportFullText(report: SiteRagReport): string {
  const md = (report as SiteRagReport & { markdown?: string }).markdown?.trim() ?? '';
  const sections = extractSections(report).map((s) => `# ${s.id}\n${s.content}`).join('\n\n');
  if (md.length > 100) return md;
  if (md.length > 0) return sections.length > 0 ? `${md}\n\n${sections}` : md;
  return sections;
}

function scanProse(
  report: SiteRagReport,
  text: string,
  source: 'prose' | 'table',
): CanonicalFactSet {
  const set: CanonicalFactSet = {};
  const lines = text.split('\n');
  const meta = {
    sourceReportId: report.id,
    sourceReportType: report.reportType ?? 'unknown',
  };

  for (const [key, patterns] of Object.entries(PROSE_PATTERNS) as [CanonicalFactKey, PatternDef[]][]) {
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]!;
      for (const pat of patterns) {
        if (pat.contextRe && !pat.contextRe.test(line)) continue;
        const distanceRole = key === 'interconnectionDistanceKm' ? 'substation' : key === 'nuclearProximityKm' ? 'reactor' : null;
        const matches = distanceRole || key === 'capacityMW' ? [...line.matchAll(new RegExp(pat.re.source, 'gi'))] : [line.match(pat.re)].filter((m): m is RegExpMatchArray => m !== null);
        for (const m of matches) {
          if (key === 'capacityMW' && isNonFacilityCapacityMention(line, m.index, m[0].length)) continue;
          if (distanceRole && classifyDistanceMention(line, m.index ?? 0, m[0].length) !== distanceRole) continue;
          // K/L — the value's CLAUSE is about a different quantity sharing
          // this unit. Window-scoped (80 back / 40 ahead of the match) so an
          // unrelated guard word elsewhere on the line can't veto a real value
          // (adversarial review 2026-08-13).
          if (!distanceRole && pat.negativeContextRe && typeof m.index === 'number') {
            const window = line.slice(Math.max(0, m.index - 80), m.index + m[0]!.length + 40);
            if (pat.negativeContextRe.test(window)) continue;
          } else if (!distanceRole && pat.negativeContextRe?.test(line)) {
            continue;
          }
          // K — a range endpoint ("200–500 MW") is a band, not a value claim.
          if (key === 'capacityMW' && isRangeEndpoint(line, m.index)) continue;
          // Pronghorn 2026-08-17 — "(<50 MW)" is a comparison BOUND, not a
          // value claim. Syntax-level, subject-agnostic (shared guard).
          if (key === 'capacityMW' && isComparisonBoundPrefix(line, m.index)) continue;
          const parsed = pat.parse(m, line);
          if (!parsed) continue;
          if (typeof parsed.value === 'number' && (!Number.isFinite(parsed.value) || parsed.value <= 0)) continue;
          pushFact(set, key, {
            ...meta,
            ...parsed,
            citationRange: lineRange(text, i),
            excerpt: line.trim().slice(0, 600),
            provenance: source,
          });
        }
      }
    }
  }

  // Markdown tables: | Capacity | 800 MW |
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    if (!line.includes('|')) continue;
    const cells = line.split('|').map((c) => c.trim()).filter(Boolean);
    if (cells.length < 2) continue;
    const label = cells[0]!.toLowerCase();
    const valCell = cells.slice(1).join(' ');

    if (/capacity|power/.test(label)) {
      const m = valCell.match(/\b([\d,]+(?:\.\d+)?)\s*(MW|GW)\b/i);
      // Pronghorn — "| Capacity | <50 MW |" states a bound, not a value.
      if (m && !isComparisonBoundPrefix(valCell, m.index)
        && !isNonFacilityCapacityMention(line, line.indexOf(m[0]), m[0].length)
        && !NON_FACILITY_MW_CONTEXT_RE.test(line)) {
        const v = parseCapacityMw(m[1], m[2]);
        if (v != null) {
          pushFact(set, 'capacityMW', {
            ...meta,
            value: v,
            units: 'MW',
            citationRange: lineRange(text, i),
            excerpt: line.trim(),
            provenance: 'table',
          });
        }
      }
    }
    if (/substation|interconnect/.test(label) && !/radius|search|buffer/.test(label)) {
      const m = valCell.match(/\b([\d,]+(?:\.\d+)?)\s*(km|mi|miles?)\b/i);
      if (m) {
        const v = parseDistanceKm(m[1], m[2]);
        if (v != null) {
          pushFact(set, 'interconnectionDistanceKm', {
            ...meta,
            value: v,
            units: 'km',
            citationRange: lineRange(text, i),
            excerpt: line.trim(),
            provenance: 'table',
          });
        }
      }
    }
    if (/acreage|acres|area/.test(label)) {
      const m = valCell.match(/\b([\d,]+(?:\.\d+)?)\s*(acres?|ac\.?|ha)\b/i);
      if (m) {
        const v = parseAreaAcres(m[1], m[2]);
        if (v != null) {
          pushFact(set, 'acreage', {
            ...meta,
            value: v,
            units: 'acres',
            citationRange: lineRange(text, i),
            excerpt: line.trim(),
            provenance: 'table',
          });
        }
      }
    }
    if (/flood/.test(label)) {
      const m = valCell.match(/\b(A(?:E|H|O)?|AE|AO|AH|VE|V|X|D|B|C)\b/i);
      if (m) {
        pushFact(set, 'floodZone', {
          ...meta,
          value: m[1].toUpperCase(),
          citationRange: lineRange(text, i),
          excerpt: line.trim(),
          provenance: 'table',
        });
      }
    }
  }

  return set;
}

function extractStructuredFacts(report: SiteRagReport): CanonicalFactSet {
  const set: CanonicalFactSet = {};
  const meta = {
    sourceReportId: report.id,
    sourceReportType: report.reportType ?? 'unknown',
    provenance: 'structured',
  };

  const d = report.datasets;
  if (d?.grid?.substationDist != null && Number.isFinite(d.grid.substationDist)) {
    pushFact(set, 'interconnectionDistanceKm', {
      ...meta,
      value: d.grid.substationDist,
      units: 'km',
      excerpt: 'datasets.grid.substationDist',
    });
  }
  if (d?.grid?.voltage) {
    const m = d.grid.voltage.match(/([\d.]+)\s*kV/i);
    // Only emit a positive voltage — a "0 kV" SED placeholder means "unknown",
    // not a tier-1 fact. Matches the guard in providerPrecedenceVerifier's
    // buildEvidenceCacheFromSed; without it the precedence banner surfaces a
    // bogus "Report 69 / Tier-1 0" conflict that wasn't a real divergence.
    if (m && parseFloat(m[1]) > 0) {
      pushFact(set, 'voltageKV', { ...meta, value: parseFloat(m[1]), units: 'kV', excerpt: 'datasets.grid.voltage' });
    }
  }
  if (d?.grid?.nearestSubstationName) {
    // F' (2026-08-14): the STRUCTURED path skipped the sanitizer, so a
    // grid-project descriptor stored in datasets ("Planned 230kV Expansion")
    // entered as a substation NAME from every report and manufactured a
    // site-wide cross-report conflict against the real name. Same reject
    // list as prose extraction — a descriptor is not a name on any path.
    const sanitized = sanitizeStructuredSubstationName(String(d.grid.nearestSubstationName));
    if (sanitized) {
      pushFact(set, 'nearestSubstation', {
        ...meta,
        value: sanitized.value,
        excerpt: 'datasets.grid.nearestSubstationName',
      });
    }
  }
  if (d?.grid?.isoRegion) {
    pushFact(set, 'gridOperator', { ...meta, value: d.grid.isoRegion, excerpt: 'datasets.grid.isoRegion' });
  }
  if (d?.land?.acres != null && Number.isFinite(d.land.acres)) {
    pushFact(set, 'acreage', { ...meta, value: d.land.acres, units: 'acres', excerpt: 'datasets.land.acres' });
  }
  if (d?.parcel?.acreage != null && Number.isFinite(d.parcel.acreage)) {
    pushFact(set, 'acreage', { ...meta, value: d.parcel.acreage, units: 'acres', excerpt: 'datasets.parcel.acreage' });
  }
  if (report.siteArea != null && Number.isFinite(report.siteArea)) {
    pushFact(set, 'acreage', { ...meta, value: report.siteArea, units: 'acres', excerpt: 'siteArea' });
  }
  if (
    d?.nrcReactors?.nearestReactorDistKm != null &&
    d.nrcReactors.nearestReactorDistKm < 900 &&
    !/^none\b/i.test(String(d.nrcReactors.nearestReactorName ?? '').trim())
  ) {
    pushFact(set, 'nuclearProximityKm', {
      ...meta,
      value: d.nrcReactors.nearestReactorDistKm,
      units: 'km',
      excerpt: 'datasets.nrcReactors.nearestReactorDistKm',
    });
  }

  return set;
}

function mergeSets(a: CanonicalFactSet, b: CanonicalFactSet): CanonicalFactSet {
  const out: CanonicalFactSet = { ...a };
  for (const [key, vals] of Object.entries(b) as [CanonicalFactKey, CanonicalFactValue[]][]) {
    for (const v of vals) pushFact(out, key, v);
  }
  return out;
}

/** Parse a single report for canonical facts (prose + structured). */
export function extractCanonicalFacts(report: SiteRagReport): CanonicalFactSet {
  const text = reportFullText(report);
  const prose = scanProse(report, text, 'prose');
  const structured = extractStructuredFacts(report);
  return mergeSets(prose, structured);
}

function extractTruthBaseline(
  site: SiteEntity,
  sed?: SiteEvidenceDocument | null,
): Partial<Record<CanonicalFactKey, CanonicalFactValue>> {
  const truth: Partial<Record<CanonicalFactKey, CanonicalFactValue>> = {};
  const base = {
    sourceReportId: 'truth',
    sourceReportType: 'baseline',
    provenance: 'provider',
  };

  // B — phased sites: the ultimate (final-phase cumulative) capacity is the
  // truth, mirroring Tier-3; phased intents often leave powerRequirementMW
  // null. Falls back to the flat requirement for unphased intents.
  const phasedUltimate = site.projectIntent
    ? capacitySsotFromPhases(effectivePhases(site.projectIntent), { requirementBucket: site.projectIntent.powerRequirement }).ultimateMW
    : undefined;
  const intentMw = phasedUltimate ?? site.projectIntent?.powerRequirementMW;
  if (intentMw != null && intentMw > 0) {
    truth.capacityMW = {
      ...base,
      value: intentMw,
      units: 'MW',
      excerpt: phasedUltimate != null
        ? 'projectIntent.phases (ultimate cumulative)'
        : 'site.projectIntent.powerRequirementMW',
    };
  }

  if (!sed) return truth;

  const grid = sed.grid?.data;
  if (grid?.substationDistKm != null && Number.isFinite(grid.substationDistKm)) {
    truth.interconnectionDistanceKm = {
      ...base,
      value: grid.substationDistKm,
      units: 'km',
      excerpt: 'sed.grid.data.substationDistKm',
    };
  }
  if (grid?.substationName) {
    truth.nearestSubstation = { ...base, value: grid.substationName, excerpt: 'sed.grid.data.substationName' };
  }
  if (grid?.voltage) {
    const m = grid.voltage.match(/([\d.]+)\s*kV/i);
    if (m) truth.voltageKV = { ...base, value: parseFloat(m[1]), units: 'kV', excerpt: 'sed.grid.data.voltage' };
  }
  if (grid?.isoRegion) {
    truth.gridOperator = { ...base, value: grid.isoRegion, excerpt: 'sed.grid.data.isoRegion' };
  }

  const flood = sed.flood?.data;
  if (flood?.floodZone && !isUnavailableFloodZone(flood.floodZone)) {
    truth.floodZone = { ...base, value: flood.floodZone.toUpperCase(), excerpt: 'sed.flood.data.floodZone' };
  }

  const acres = sed.siteProfile?.data?.acres;
  if (acres != null && Number.isFinite(acres) && acres > 0) {
    truth.acreage = { ...base, value: acres, units: 'acres', excerpt: 'sed.siteProfile.data.acres' };
  }

  const env = sed.environmental?.data;
  if (env?.wetlandAcres != null && acres != null && acres > 0) {
    truth.wetlandPercent = {
      ...base,
      value: Math.round((env.wetlandAcres / acres) * 1000) / 10,
      units: '%',
      excerpt: 'sed.environmental.data.wetlandAcres / siteProfile.acres',
    };
  }

  const nrc = sed.nuclearInfra?.data;
  if (
    nrc?.nearestReactorDistKm != null &&
    Number.isFinite(nrc.nearestReactorDistKm) &&
    nrc.nearestReactorDistKm < 900 &&
    !/^none\b/i.test(String(nrc.nearestReactorName ?? '').trim())
  ) {
    truth.nuclearProximityKm = {
      ...base,
      value: nrc.nearestReactorDistKm,
      units: 'km',
      excerpt: 'sed.nuclearInfra.data.nearestReactorDistKm',
    };
  }

  return truth;
}

/**
 * R-79 — canonical fact keys the user has already RESOLVED via a persisted
 * `site.auditOverrides` entry. A settled fact must not re-surface as a
 * cross-report divergence on the next audit run.
 *
 * Two override shapes settle a canonical fact (same plumbing the SiteAuditTab
 * records under — see `applyReconcile` / `handleValidate`):
 *  - `type: 'reconcile'` — the user promoted ONE value to canonical; its
 *    `canonicalKey` is the `CanonicalFactKey` (e.g. `'capacityMW'`).
 *  - `type: 'validate'`  — the user affirmed the value is correct as-is; its
 *    `factKey` is the human label (`FACT_LABELS[key]`, e.g. `'Capacity (MW)'`),
 *    so we match against both the raw key and its label.
 *
 * Matching is case-insensitive on the trimmed string to tolerate label drift.
 */
function settledFactKeys(
  rawOverrides: SiteAuditOverride[] | undefined,
): Set<CanonicalFactKey> {
  const settled = new Set<CanonicalFactKey>();
  const overrides = resolveActiveAuditOverrides(rawOverrides);
  if (!overrides.length) return settled;

  const allKeys = Object.keys(FACT_LABELS) as CanonicalFactKey[];
  const norm = (s: string): string => s.trim().toLowerCase();
  const keyByAlias = new Map<string, CanonicalFactKey>();
  for (const k of allKeys) {
    keyByAlias.set(norm(k), k);
    keyByAlias.set(norm(FACT_LABELS[k]), k);
  }

  for (const o of overrides) {
    const aliases: string[] = [];
    if ('type' in o && o.type === 'reconcile' && o.canonicalKey) aliases.push(o.canonicalKey);
    else if ('type' in o && o.type === 'validate') {
      const a = o.factKey ?? o.checkId;
      if (a) aliases.push(a);
    } else if ('gate' in o && Array.isArray((o as { fields?: string[] }).fields)) {
      // Legacy provider-precedence waivers ({gate, fields, rationale}) are
      // fact decisions too — Redhead carried FOUR recorded
      // interconnectionDistanceKm waivers ("use the Azimuth KMZ value") yet
      // R-79 kept warning on that fact because this loop only read the
      // modern shapes. A decided fact is settled regardless of shape vintage.
      aliases.push(...((o as { fields?: string[] }).fields ?? []));
    }
    for (const alias of aliases) {
      const matched = keyByAlias.get(norm(alias));
      if (matched) settled.add(matched);
    }
  }
  return settled;
}

function numericTolerance(key: CanonicalFactKey): { pct?: number; abs?: number } {
  switch (key) {
    case 'capacityMW': return { pct: 0.01 };
    case 'interconnectionDistanceKm': return { abs: 0.5 };
    case 'nuclearProximityKm': return { abs: 1.0 };
    case 'acreage': return { pct: 0.05 };
    case 'wetlandPercent': return { pct: 0.05 };
    case 'voltageKV': return { pct: 0.02 };
    default: return {};
  }
}

function valuesAgree(key: CanonicalFactKey, a: number, b: number): boolean {
  const tol = numericTolerance(key);
  if (tol.abs != null) return Math.abs(a - b) <= tol.abs;
  if (tol.pct != null) {
    const denom = Math.max(Math.abs(a), Math.abs(b), 1e-9);
    return Math.abs(a - b) / denom <= tol.pct;
  }
  return a === b;
}

function normalizeFloodZoneToken(raw: string): string {
  return raw
    .trim()
    .toUpperCase()
    .replace(/^(?:FEMA\s+)?(?:100[- ]YR\s+)?(?:FLOOD\s+)?(?:ZONE\s+)?/, '')
    .trim();
}

function stringAgree(key: CanonicalFactKey, a: string, b: string): boolean {
  if (key === 'floodZone') {
    return normalizeFloodZoneToken(a) === normalizeFloodZoneToken(b);
  }
  if (key === 'censusTract') {
    return a.trim().toUpperCase() === b.trim().toUpperCase();
  }
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

function pickRepresentative(values: CanonicalFactValue[]): CanonicalFactValue {
  return values[0]!;
}

function formatValue(v: CanonicalFactValue): string {
  return v.units ? `${v.value} ${v.units}` : String(v.value);
}

function buildRemediation(
  key: CanonicalFactKey,
  byReport: Record<string, CanonicalFactValue>,
  truth?: CanonicalFactValue,
): string {
  const parts = Object.entries(byReport).map(
    ([rt, v]) => `${rt} uses ${formatValue(v)}`,
  );
  const hint = parts.join('; ');
  if (truth) {
    return `${hint} — tier-1 baseline is ${formatValue(truth)} (${truth.excerpt ?? 'SED/intent'}). ${RECHECK_HINT_BY_FACT[key] ?? DEFAULT_RECHECK_HINT}`;
  }
  return `${hint} — ${RECHECK_HINT_BY_FACT[key] ?? DEFAULT_RECHECK_HINT}`;
}

/**
 * Where to look to settle each fact (2026-08-14). Every conflict used to end
 * with "recheck NREL/HIFLD substation table" — sent an operator resolving a
 * WATER SOURCE or FLOOD ZONE disagreement to the electrical substation table.
 * Point at the source that actually governs the fact.
 */
const DEFAULT_RECHECK_HINT = 'recheck the SED provider slots for this fact.';
const RECHECK_HINT_BY_FACT: Partial<Record<CanonicalFactKey, string>> = {
  capacityMW: 'reconcile against the project intent phase plan (the capacity SSOT).',
  interconnectionDistanceKm: 'recheck the HIFLD substation table and sed.grid.data.substationDistKm.',
  nearestSubstation: 'recheck the HIFLD substation table and sed.grid.data.substationName.',
  voltageKV: 'recheck the HIFLD substation table and sed.grid.data.voltage.',
  gridOperator: 'recheck sed.grid.data.isoRegion (ISO/RTO only — the serving utility is a different fact).',
  floodZone: 'recheck the FEMA NFHL panel and sed.flood.data.floodZone.',
  wetlandPercent: 'recheck the USFWS NWI layer and sed.environmental.data.wetlandAcres.',
  acreage: 'recheck the parcel geometry and sed.siteProfile.data.acres.',
  waterSourceType: 'confirm the declared water source(s) on the Intent tab — it is a multi-select, and every declared source is valid.',
  nuclearProximityKm: 'recheck the NRC reactor list and sed.nuclear provider slot.',
  censusTract: 'recheck the Census ACS provider slot.',
};

/** Compare facts across reports and against site intent + SED truth. */
export function reconcileSiteFacts(
  site: SiteEntity,
  reports: SiteRagReport[],
  sed?: SiteEvidenceDocument | null,
): ReconciliationReport {
  const factsByReport: Record<string, CanonicalFactSet> = {};
  for (const r of reports) {
    factsByReport[r.id] = extractCanonicalFacts(r);
  }

  const truthBaseline = extractTruthBaseline(site, sed);
  const findings: ReconciliationFinding[] = [];

  // R-79 — a fact the user already RESOLVED (reconciled or validated) via a
  // persisted auditOverride is settled and must be suppressed from divergence.
  const settled = settledFactKeys(site.auditOverrides);

  // B — the same phase anchors Tier-2e/Tier-3 corroborate against: ultimate +
  // per-phase cumulative ramp + ramp-sum echo. A capacity mention agreeing
  // with ANY anchor is phase-consistent, never a divergence or dissent.
  const phaseAnchorsMW = capacityAnchors(
    site.projectIntent
      ? capacitySsotFromPhases(effectivePhases(site.projectIntent), { requirementBucket: site.projectIntent.powerRequirement })
      : undefined,
  );
  const onPhaseRamp = (v: string | number): boolean =>
    typeof v === 'number' && phaseAnchorsMW.some((a) => valuesAgree('capacityMW', v, a));

  // Water source is MULTI-select in the intent (2026-08-13, Redhead RCA:
  // municipal + surface + reuse all declared). Two reports each naming a
  // DIFFERENT declared source are both narrating the supply strategy, not
  // disagreeing — declared kinds agree with each other. A value OUTSIDE the
  // declared set (Redhead: an invented "groundwater") still conflicts.
  const declaredWaterValues = new Set<string>();
  for (const kind of waterSourceKinds(site.projectIntent?.waterSource)) {
    for (const v of WATER_KIND_PROSE_VALUES[kind] ?? [kind]) declaredWaterValues.add(v);
  }
  const bothDeclaredWater = (a: string, b: string): boolean =>
    declaredWaterValues.has(a.toLowerCase()) && declaredWaterValues.has(b.toLowerCase());

  const keys = new Set<CanonicalFactKey>();
  for (const set of Object.values(factsByReport)) {
    for (const k of Object.keys(set) as CanonicalFactKey[]) keys.add(k);
  }

  for (const key of keys) {
    if (settled.has(key)) continue;
    const byReportType = new Map<string, CanonicalFactValue>();
    for (const r of reports) {
      const vals = factsByReport[r.id]?.[key];
      if (!vals?.length) continue;
      byReportType.set(r.reportType ?? r.id, pickRepresentative(vals));
    }
    if (byReportType.size < 2) continue;

    const distinct: CanonicalFactValue[] = [];
    for (const v of byReportType.values()) {
      // B — anchor-corroborated capacity values are consistent by definition.
      if (key === 'capacityMW' && onPhaseRamp(v.value)) continue;
      const matched = distinct.some((d) => {
        if (typeof d.value === 'number' && typeof v.value === 'number') {
          return valuesAgree(key, d.value, v.value);
        }
        if (key === 'waterSourceType' && bothDeclaredWater(String(d.value), String(v.value))) {
          return true;
        }
        return stringAgree(key, String(d.value), String(v.value));
      });
      if (!matched) distinct.push(v);
    }
    if (distinct.length < 2) continue;

    const reportValues: Record<string, CanonicalFactValue> = {};
    for (const [rt, v] of byReportType) reportValues[rt] = v;

    const truth = truthBaseline[key];
    let severity: ReconciliationFinding['severity'] = 'warn';
    if (truth) {
      const anyVsTruth = [...byReportType.values()].some((v) => {
        if (typeof v.value === 'number' && typeof truth.value === 'number') {
          return !valuesAgree(key, v.value, truth.value as number);
        }
        return !stringAgree(key, String(v.value), String(truth.value));
      });
      if (anyVsTruth) severity = 'block';
    }

    // Near-miss within tolerance → info only
    if (typeof distinct[0]?.value === 'number' && typeof distinct[1]?.value === 'number') {
      const allClose = distinct.every((d, _, arr) =>
        arr.every((o) =>
          typeof d.value === 'number' && typeof o.value === 'number'
            ? valuesAgree(key, d.value, o.value)
            : false,
        ),
      );
      if (allClose) continue;
      const spread = Math.max(...distinct.map((d) => d.value as number))
        - Math.min(...distinct.map((d) => d.value as number));
      const tol = numericTolerance(key);
      if (tol.pct != null && key === 'capacityMW') {
        const max = Math.max(...distinct.map((d) => d.value as number));
        if (spread / max <= tol.pct) {
          severity = 'info';
        }
      }
    }

    findings.push({
      factKey: key,
      severity,
      message: `${FACT_LABELS[key]} disagrees across reports: ${Object.entries(reportValues).map(([rt, v]) => `${rt}=${formatValue(v)}`).join(', ')}.`,
      remediationHint: buildRemediation(key, reportValues, truth),
      reportValues,
      truthValue: truth
        ? { value: truth.value, source: truth.excerpt ?? 'baseline' }
        : undefined,
    });
  }

  // Intent-only check when no cross-report conflict but report vs intent diverges
  const intentMw = truthBaseline.capacityMW;
  if (intentMw && !settled.has('capacityMW')) {
    for (const r of reports) {
      const caps = factsByReport[r.id]?.capacityMW ?? [];
      for (const cap of caps) {
        if (typeof cap.value !== 'number') continue;
        // B — a per-phase cumulative value is consistent with a phased intent;
        // warning on every phase mention made phased sites permanently noisy.
        if (onPhaseRamp(cap.value)) continue;
        if (!valuesAgree('capacityMW', cap.value, intentMw.value as number)) {
          const already = findings.some(
            (f) => f.factKey === 'capacityMW' && f.reportValues[r.reportType ?? '']?.value === cap.value,
          );
          if (!already) {
            findings.push({
              factKey: 'capacityMW',
              severity: 'warn',
              message: `${r.reportType} cites ${cap.value} MW but project intent is ${intentMw.value} MW.`,
              remediationHint: `Reconcile capacity with site.projectIntent (${intentMw.excerpt}).`,
              reportValues: { [r.reportType ?? r.id]: cap },
              truthValue: { value: intentMw.value, source: intentMw.excerpt ?? 'intent' },
            });
          }
        }
      }
    }
  }

  findings.sort((a, b) => {
    const rank = { block: 0, warn: 1, info: 2 };
    return rank[a.severity] - rank[b.severity];
  });

  return {
    siteId: site.id,
    findings,
    factsByReport,
    truthBaseline,
  };
}

/** Render reconciliation report as markdown (CLI / audit script). */
export function renderReconciliationMarkdown(
  siteName: string,
  report: ReconciliationReport,
): string {
  const lines: string[] = [];
  lines.push(`### Canonical Reconciliation — ${siteName} (\`${report.siteId}\`)`);
  lines.push('');
  if (report.findings.length === 0) {
    lines.push('- ✅ No cross-report canonical fact disagreements detected (within tolerance).');
    lines.push('');
    return lines.join('\n');
  }
  for (const f of report.findings) {
    const badge = f.severity === 'block' ? '🔴 BLOCK' : f.severity === 'warn' ? '🟡 WARN' : 'ℹ INFO';
    lines.push(`#### ${badge} — ${FACT_LABELS[f.factKey]}`);
    lines.push(f.message);
    lines.push(`> ${f.remediationHint}`);
    lines.push('');
    lines.push('| Report type | Value | Source |');
    lines.push('| --- | --- | --- |');
    for (const [rt, v] of Object.entries(f.reportValues)) {
      const loc = v.citationRange
        ? `L${v.citationRange.startLine}${v.citationRange.endLine !== v.citationRange.startLine ? `–L${v.citationRange.endLine}` : ''}`
        : (v.excerpt ?? '—');
      lines.push(`| ${rt} | ${formatValue(v)} | \`${v.sourceReportId}\` ${loc} |`);
    }
    if (f.truthValue) {
      lines.push(`| **baseline** | ${f.truthValue.value} | ${f.truthValue.source} |`);
    }
    lines.push('');
  }
  return lines.join('\n');
}
